import { useEffect, useState, useMemo } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getRefresherSchedule, GetRefresherScheduleOutputType, requestReassessment } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, CalendarClock, CheckCircle, Clock, RefreshCw, Search, User, BookOpen, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

type Data = GetRefresherScheduleOutputType;
type Item = Data['items'][0];

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; cls: string }> = {
    overdue: { label: 'Overdue', cls: 'bg-destructive/10 text-destructive border-destructive/30' },
    due_soon: { label: 'Due Soon', cls: 'bg-orange-500/10 text-orange-600 border-orange-300' },
    upcoming: { label: 'Upcoming', cls: 'bg-primary/10 text-primary border-primary/30' },
    reassessed: { label: 'Reassessed', cls: 'bg-green-500/10 text-green-700 border-green-300' },
  };
  const c = cfg[status] || cfg.upcoming;
  return <Badge variant="outline" className={`text-xs ${c.cls}`}>{c.label}</Badge>;
}

function CountCard({ icon: Icon, label, count, color, active, onClick }: {
  icon: typeof Clock; label: string; count: number; color: string; active: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} className={`border rounded-lg p-3 text-left transition-all ${active ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border bg-card hover:border-primary/40'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-foreground">{count}</p>
    </button>
  );
}

function RefresherRow({ item, onReassess }: { item: Item; onReassess: (item: Item) => void }) {
  const dueStr = item.refresherDueDate ? format(new Date(item.refresherDueDate), 'd MMM yyyy') : '—';
  const approvedStr = item.approvedDate ? format(new Date(item.approvedDate), 'd MMM yyyy') : '—';
  const daysLabel = item.daysUntilDue < 0
    ? `${Math.abs(item.daysUntilDue)}d overdue`
    : item.daysUntilDue === 0 ? 'Due today'
    : `${item.daysUntilDue}d`;

  return (
    <div className="border border-border rounded-lg bg-card p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{item.employeeName}</p>
            <p className="text-xs text-muted-foreground truncate">{item.courseTitle} — {item.moduleLabel}</p>
          </div>
        </div>
        <StatusBadge status={item.status} />
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved {approvedStr}</span>
          <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Due {dueStr}</span>
        </div>
        <span className={`font-semibold ${item.daysUntilDue < 0 ? 'text-destructive' : item.daysUntilDue <= 30 ? 'text-orange-600' : 'text-muted-foreground'}`}>
          {daysLabel}
        </span>
      </div>
      {!item.hasReassessment && item.status !== 'reassessed' && (
        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => onReassess(item)}>
            <RotateCcw className="w-3 h-3" /> Schedule Reassessment
          </Button>
        </div>
      )}
      {item.hasReassessment && (
        <p className="text-xs text-green-600 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reassessment in progress</p>
      )}
    </div>
  );
}

export default function RefresherSchedule() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [reassessItem, setReassessItem] = useState<Item | null>(null);
  const [reassessing, setReassessing] = useState(false);

  const load = () => {
    if (!user) return;
    setLoading(true);
    getRefresherSchedule({
      statusFilter: statusFilter as any,
      courseFilter: courseFilter !== 'all' ? courseFilter : undefined,
      search: search || undefined,
    })
      .then(setData)
      .catch(() => toast.error('Failed to load refresher schedule'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user, statusFilter, courseFilter]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleReassess = async () => {
    if (!reassessItem) return;
    setReassessing(true);
    try {
      const result = await requestReassessment({
        enrollmentId: reassessItem.enrollmentId,
        moduleId: reassessItem.moduleId,
        originalSessionId: reassessItem.sessionId,
      });
      toast.success(result.message || `Reassessment created for ${reassessItem.employeeName}`);
      setReassessItem(null);
      load();
    } catch (e: any) {
      const msg = e?.message || e?.toString?.() || 'Failed to request reassessment';
      console.error('Reassessment error:', e);
      toast.error(msg);
    } finally {
      setReassessing(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="px-6 py-8 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const counts = data?.counts || { total: 0, overdue: 0, dueSoon: 0, upcoming: 0, reassessed: 0 };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 md:px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary" />
              Refresher Schedule
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">6-month practical assessment refresher due dates and reassessment tracking</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-5xl mx-auto space-y-6">
        {/* Count cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <CountCard icon={BookOpen} label="Total" count={counts.total} color="text-primary" active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
          <CountCard icon={AlertTriangle} label="Overdue" count={counts.overdue} color="text-destructive" active={statusFilter === 'overdue'} onClick={() => setStatusFilter('overdue')} />
          <CountCard icon={Clock} label="Due Soon" count={counts.dueSoon} color="text-orange-500" active={statusFilter === 'due_soon'} onClick={() => setStatusFilter('due_soon')} />
          <CountCard icon={CalendarClock} label="Upcoming" count={counts.upcoming} color="text-blue-500" active={statusFilter === 'upcoming'} onClick={() => setStatusFilter('upcoming')} />
          <CountCard icon={RefreshCw} label="Reassessed" count={counts.reassessed} color="text-green-500" active={statusFilter === 'reassessed'} onClick={() => setStatusFilter('reassessed')} />
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="Search employee/course..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
          </div>
          {data && data.courses.length > 0 && (
            <Select value={courseFilter} onValueChange={setCourseFilter}>
              <SelectTrigger className="w-48 h-9 text-xs"><SelectValue placeholder="Filter by course" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {data.courses.map(c => <SelectItem key={c.id} value={c.courseCode || c.id}>{c.title} ({c.courseCode})</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Items list */}
        {data && data.items.length === 0 ? (
          <div className="text-center py-16">
            <CalendarClock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {counts.total === 0 ? 'No approved assessments found — refresher dates are calculated from approved practical assessments.' : 'No items match your filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data?.items.map(item => (
              <RefresherRow key={item.sessionId} item={item} onReassess={setReassessItem} />
            ))}
          </div>
        )}
      </div>

      {/* Reassessment confirmation dialog */}
      {reassessItem && (
        <Dialog open onOpenChange={v => { if (!v && !reassessing) setReassessItem(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Schedule Reassessment</DialogTitle>
              <DialogDescription>
                This will create a new assessment session linked to the original approved session for <strong>{reassessItem.employeeName}</strong> on <strong>{reassessItem.moduleLabel}</strong>.
                The original approved assessment will be preserved as evidence.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-1">
              <p><strong>Employee:</strong> {reassessItem.employeeName}</p>
              <p><strong>Course:</strong> {reassessItem.courseTitle} ({reassessItem.courseCode})</p>
              <p><strong>Module:</strong> {reassessItem.moduleLabel}</p>
              <p><strong>Original session:</strong> {reassessItem.sessionId.slice(0, 8)}…</p>
              <p><strong>Original approval:</strong> {reassessItem.approvedDate ? format(new Date(reassessItem.approvedDate), 'd MMM yyyy') : '—'}</p>
              <p><strong>Refresher due:</strong> {reassessItem.refresherDueDate ? format(new Date(reassessItem.refresherDueDate), 'd MMM yyyy') : '—'}</p>
            </div>
            <p className="text-xs text-muted-foreground">This will create a new assessment session. No real notifications will be sent.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassessItem(null)} disabled={reassessing}>Cancel</Button>
              <Button onClick={handleReassess} disabled={reassessing} className="gap-1.5">
                {reassessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                {reassessing ? 'Creating…' : 'Confirm Reassessment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
